import { ComponentInterface, Entity, lerp } from '@goodboydigital/odie';

import { Random } from '../../utils/Random';
import { CharacterEntity } from '../entities/CharacterEntity';
import { PhysicsComponent } from '../physics';

export enum ReplayBodyMode
    {
    IDLE,
    RECORD,
    PLAYBACK,
}

export class ReplayBodyComponent implements ComponentInterface
{
    public physics?: PhysicsComponent;
    public mode: ReplayBodyMode;
    public playing = false;

    /** Sequence of values to be converted into x/y/angle */
    public buffer: number[];

    /** Buffer position */
    public position = 0;

    /** Number of values per record step - x, y and angle */
    public itemSize = 3;

    /** Number of sub-steps between items - interpolate X times between A and B */
    public stepSize = 4;

    /** Suspend body x and y update */
    public suspendPlayback = false;

    public playbackOffsetX = 0;
    public playbackOffsetY = 3;

    constructor()
    {
        this.mode = ReplayBodyMode.IDLE;
        this.buffer = [];
    }

    public reset(): void
    {
        this.mode = ReplayBodyMode.IDLE;
        this.position = 0;
        this.playing = false;
    }

    public addedToEntity(entity: Entity): void
    {
        const physics = entity.getComponent(PhysicsComponent);

        if (physics)
        {
            this.physics = physics;
            this.physics.physicsFixedUpdateRunner.add(this as any);
        }
    }

    public physicsFixedUpdate(): void
    {
        if (!this.physics) return;
        if (this.mode === ReplayBodyMode.RECORD)
        {
            this.recordStep();
            this.position += 1;
        }
        else if (this.mode === ReplayBodyMode.PLAYBACK)
        {
            this.playbackStep();
            this.position += 1;

            const end = (this.buffer.length / this.itemSize * this.stepSize) - 1;

            if (this.position >= end) this.position = end;
        }
    }

    public startRecording(): void
    {
        if (!this.physics || this.mode === ReplayBodyMode.RECORD) return;
        this.position = 0;
        this.buffer = [];
        this.mode = ReplayBodyMode.RECORD;
    }

    public stopRecording(): void
    {
        if (!this.physics || this.mode !== ReplayBodyMode.RECORD) return;
        this.mode = ReplayBodyMode.IDLE;
    }

    public startPlayback(): void
    {
        if (!this.physics || this.mode === ReplayBodyMode.PLAYBACK) return;
        this.position = 0;
        this.mode = ReplayBodyMode.PLAYBACK;
    }

    public stopPlayback(): void
    {
        if (!this.physics || this.mode !== ReplayBodyMode.PLAYBACK) return;
        this.mode = ReplayBodyMode.IDLE;
    }

    public setBuffer(buffer: number[], stepSize: number): void
    {
        this.stepSize = stepSize;
        this.position = 0;
        this.buffer = buffer;
    }

    private recordStep(): void
    {
        if (!this.physics) return;
        const body = this.physics.mainBody;

        this.buffer.push(body.position[0]);
        this.buffer.push(body.position[1]);
        this.buffer.push(body.angle);
    }

    public playbackStep(position?: number): void
    {
        if (!this.physics || this.suspendPlayback) return;
        if (position) this.position = position;
        const body = this.physics.mainBody;

        // Buffer position considering the step size
        const pos = Math.floor(this.position / this.stepSize);

        // Buffer base index
        const i = Math.min(pos * this.itemSize, this.buffer.length - (this.itemSize * 2));

        // Current positions/values
        const a0 = this.buffer[i] + this.playbackOffsetX;
        const a1 = this.buffer[i + 1] + this.playbackOffsetY;
        const a2 = this.buffer[i + 2];

        if (this.stepSize === 1)
        {
            // No need for intepolation if step size is 1
            body.position[0] = a0;
            body.position[1] = a1;
            body.angle = a2;

            return;
        }

        // Next positions/values
        const b0 = this.buffer[i + 3] + this.playbackOffsetX;
        const b1 = this.buffer[i + 4] + this.playbackOffsetY;
        const b2 = this.buffer[i + 5];

        // Interrpolation step
        const t = (1 / this.stepSize) * (this.position % this.stepSize);

        // Interpolate each prropety between A and B
        body.position[0] = lerp(a0, b0, t);
        body.position[1] = lerp(a1, b1, t);
        body.angle = lerp(a2, b2, t);
    }

    public hide(): void
    {
        if (!this.physics) return;
        this.suspendPlayback = true;

        this.physics.y = -this.buffer[1] - Random.range(200, 250);
        this.physics.entity.view.alpha = 0;
    }

    public async dropFromTheSky(delay: number): Promise<void>
    {
        if (!this.physics || !this.physics.entity.game) return;
        this.suspendPlayback = true;
        const y = -this.buffer[1] - this.playbackOffsetY;

        await this.physics.entity.game.tween.to(this.physics, {
            y, delay,
            duration: 0.7,
            ease: 'back.in',
            onStart: () =>
            {
                if (!this.physics) return;
                this.physics.entity.view.alpha = 1;
                if (this.physics.entity instanceof CharacterEntity)
                {
                    this.physics.entity.game?.effects.playBash(this.physics);
                }
            },
        });
        this.suspendPlayback = false;
    }
}
